import { ExclamationTriangleIcon, InformationCircleIcon, MagnifyingGlassCircleIcon } from '@heroicons/react/24/outline'
import { useCallback, useEffect, useState } from 'react'
import { getBorrowerDrawAdvisory, getDrawAdvisory } from 'services'
import { formatDate } from 'utils'

/**
 * What a draw screen tells the person entering a draw, before they enter it.
 *
 * Two things, and neither of them stops anything:
 *
 * 1. **The inspection.** A draw inspection is an ordinary valuation order; nothing in the platform
 *    orders one when a draw request arrives and nothing requires one before a draw is issued, yet
 *    the borrower portal's draw window tells the borrower an inspection is being requested. So
 *    every draw surface states what actually exists, with its status and a link to the order.
 * 2. **The reasons to look twice.** Delinquency, maturity, lapsed insurance, a servicing Hold,
 *    foreclosure, bankruptcy, an active payoff, an unsigned lien waiver - each a reason a servicer
 *    would normally refuse a draw, none of them enforced anywhere in the draw path.
 *
 * Deliberately not a gate, and the panel says so in as many words. The trust ledger records what
 * happened; it does not decide what may happen. The figures come from one backend route
 * (`/loan/drawAdvisory/:loanNumber`) so the loan file, the pipeline modal and the borrower's own
 * window cannot each quote a different answer.
 */

/** One draw inspection order on the loan. */
export interface DrawInspection {
  source: 'valuation' | 'richerValues'
  id: number
  vendor: string
  orderType: string
  /** Which draw the vendor was told this is for, where it records one. */
  drawNumber: string
  status: string
  statusLabel: string
  state: 'open' | 'complete' | 'cancelled'
  orderedAt: string
  inspectionDate: string
  completedAt: string
  reportCount: number
}

/** `stop` is a reason a servicer would normally refuse; `warn` is something to know first. */
export interface DrawFlag {
  key: string
  severity: 'stop' | 'warn'
  label: string
  detail: string
}

export interface DrawAdvisoryData {
  inspections: DrawInspection[]
  latest: DrawInspection | null
  /** Absent on the borrower's own view, which carries no servicing flags. */
  flags?: DrawFlag[]
  /** Borrower view only. */
  lienWaiverStatus?: string
  /**
   * Whether the backend answered as the lender's own staff. These draw screens are reached by
   * third parties too - an external originator opens the same Draw Request screen a draw
   * specialist does - and for them the route withholds the flags and this is false, which is
   * also what drops the staff links out of the panel.
   */
  staffView?: boolean
}

const EMPTY: DrawAdvisoryData = { inspections: [], latest: null, flags: [], staffView: false }

/**
 * Where a flag is dealt with. Opened in a new tab on purpose: every one of these links is offered
 * from a draw form that is half filled in, and navigating away from it would throw the entry away.
 *
 * Keyed on the flag key the backend sends, so a flag with nowhere to go simply has no link.
 */
const FLAG_LINKS: Record<string, string> = {
  paymentDefault: 'loanPayments',
  pastDue: 'loanPayments',
  matured: 'servicingNotes',
  servicingStatus: 'servicingNotes',
  servicingStatusSold: 'servicingNotes',
  hold: 'servicingNotes',
  insurance: 'servicingNotes',
  foreclosure: 'defaultTracking',
  bankruptcy: 'defaultTracking',
  payoff: 'payoff',
}

/** The Valuations screen for the loan, which is where every inspection order is read. */
export const inspectionOrderLink = (loanNumber: number) => `/order_valuation/${loanNumber}`

const flagLink = (flag: DrawFlag, loanNumber: number): string => {
  if (flag.key === 'noInspection' || flag.key === 'inspectionOpen') return inspectionOrderLink(loanNumber)
  const menu = FLAG_LINKS[flag.key]
  return menu ? `/servicing/${loanNumber}?menu=${menu}` : ''
}

/** One line naming an inspection: who, what, and the date it was ordered. */
export const drawInspectionLabel = (inspection: DrawInspection): string =>
  [
    inspection.vendor || 'Inspection',
    inspection.drawNumber ? `draw ${inspection.drawNumber}` : '',
    inspection.orderedAt ? `ordered ${formatDate(inspection.orderedAt)}` : '',
    inspection.statusLabel,
  ]
    .filter(Boolean)
    .join(' · ')

/**
 * The inspections as a select's options, for the field on the draw entry forms that records which
 * one a draw was taken against. Cancelled orders are offered too: a draw may well have been taken
 * against an order that was cancelled afterwards, and hiding it would make the row unreadable.
 */
export const drawInspectionOptions = (inspections: DrawInspection[]): Record<string, string> =>
  Object.fromEntries((inspections || []).map((i) => [`${i.source}:${i.id}`, drawInspectionLabel(i)]))

/** The select's value for one inspection, and the pair it is stored as on the draw row. */
export const inspectionOptionKey = (inspection: DrawInspection) => `${inspection.source}:${inspection.id}`

export const parseInspectionOptionKey = (value: string) => {
  const [source, id] = String(value || '').split(':')
  const orderId = Number(id)
  if (!(orderId > 0)) return null
  return { inspectionOrderSource: source === 'richerValues' ? 'richerValues' : 'valuation', inspectionOrderId: orderId }
}

/**
 * The select key for the inspection a draw row already points at, or '' when it points at none.
 * The inverse of `parseInspectionOptionKey`, for seeding the field when a row is opened to edit.
 */
export const inspectionKeyOfRow = (
  row: { inspectionOrderId?: number; inspectionOrderSource?: string } | null | undefined,
): string => {
  const id = Number(row?.inspectionOrderId) || 0
  if (!id) return ''
  return `${row?.inspectionOrderSource === 'richerValues' ? 'richerValues' : 'valuation'}:${id}`
}

/** The inspection a draw row points at, matched back to the loan's orders. */
export const inspectionForDraw = (
  inspections: DrawInspection[],
  row: { inspectionOrderId?: number; inspectionOrderSource?: string } | null | undefined,
): DrawInspection | null => {
  const id = Number(row?.inspectionOrderId) || 0
  if (!id) return null
  const source = row?.inspectionOrderSource === 'richerValues' ? 'richerValues' : 'valuation'
  return (inspections || []).find((i) => i.id === id && i.source === source) || null
}

/**
 * Load the advisory for one loan. A failed lookup leaves the panel empty rather than taking the
 * draw screen down with it - the same rule the payoff guard and the Hold banner follow.
 *
 * `forBorrower` reads the borrower-safe route instead, which carries the inspection and the lien
 * waiver and none of the servicing flags.
 */
export const useDrawAdvisory = (
  loanNumber?: number | null,
  forBorrower = false,
  drawScope: { drawSource?: 'request' | 'ledger' | ''; drawId?: number } = {},
) => {
  const [advisory, setAdvisory] = useState<DrawAdvisoryData | null>(null)
  const [loading, setLoading] = useState(false)
  // Pulled apart so the effect depends on two primitives rather than an object literal the caller
  // rebuilds on every render, which would reload the advisory in a loop.
  const { drawSource = '', drawId = 0 } = drawScope

  const load = useCallback(() => {
    if (!forBorrower && !(Number(loanNumber) > 0)) {
      setAdvisory(null)
      return
    }
    setLoading(true)
    const scope = drawSource && Number(drawId) > 0 ? { drawSource, drawId: Number(drawId) } : {}
    const request = forBorrower ? getBorrowerDrawAdvisory(scope) : getDrawAdvisory(Number(loanNumber), scope)
    request
      .then((res: any) =>
        setAdvisory({
          inspections: Array.isArray(res?.inspections) ? res.inspections : [],
          latest: res?.latest || null,
          flags: Array.isArray(res?.flags) ? res.flags : [],
          lienWaiverStatus: res?.lienWaiverStatus || '',
          staffView: res?.staffView === true,
        }),
      )
      .catch(() => setAdvisory(EMPTY))
      .finally(() => setLoading(false))
  }, [loanNumber, forBorrower, drawSource, drawId])

  useEffect(load, [load])

  return { advisory, loading, reload: load }
}

const Line = ({ children }: { children: React.ReactNode }) => <div className="text-[13px] leading-5">{children}</div>

const inspectionTone = (inspection: DrawInspection | null) => {
  if (!inspection) return 'text-gray-500'
  if (inspection.state === 'complete') return 'text-green-700'
  if (inspection.state === 'cancelled') return 'text-gray-500'
  return 'text-amber-700'
}

/** What the inspection section says, whether or not there is one. */
const InspectionSummary = ({
  advisory,
  loanNumber,
  staffView,
}: {
  advisory: DrawAdvisoryData
  loanNumber: number
  /** Staff get the links into Valuations; a borrower or a third party is only told what exists. */
  staffView: boolean
}) => {
  const latest = advisory.latest
  const others = (advisory.inspections || []).filter((i) => !latest || i.id !== latest.id || i.source !== latest.source)

  if (!latest)
    return (
      <div className="flex items-start gap-2">
        <MagnifyingGlassCircleIcon className="w-4 h-4 mt-0.5 shrink-0 text-gray-400" />
        <div>
          <Line>
            <span className="font-variation-settings-600">No draw inspection has been ordered</span> on this loan.
          </Line>
          {staffView && (
            <Line>
              <a
                href={inspectionOrderLink(loanNumber)}
                target="_blank"
                rel="noreferrer"
                className="text-shade-blue hover:underline"
              >
                Order one
              </a>
              <span className="text-gray-500"> - it is not required to record the draw.</span>
            </Line>
          )}
        </div>
      </div>
    )

  return (
    <div className="flex items-start gap-2">
      <MagnifyingGlassCircleIcon className={`w-4 h-4 mt-0.5 shrink-0 ${inspectionTone(latest)}`} />
      <div>
        <Line>
          <span className="font-variation-settings-600">{latest.orderType || 'Draw inspection'}</span>
          <span className="text-gray-500">{latest.vendor ? ` with ${latest.vendor}` : ''}</span>
          <span className={inspectionTone(latest)}> · {latest.statusLabel}</span>
          {staffView && (
            <>
              {' · '}
              <a
                href={inspectionOrderLink(loanNumber)}
                target="_blank"
                rel="noreferrer"
                className="text-shade-blue hover:underline"
              >
                View order
              </a>
            </>
          )}
        </Line>
        <Line>
          <span className="text-gray-500">
            {[
              latest.drawNumber ? `Draw ${latest.drawNumber}` : '',
              latest.orderedAt ? `ordered ${formatDate(latest.orderedAt)}` : '',
              latest.inspectionDate ? `inspected ${formatDate(latest.inspectionDate)}` : '',
              latest.completedAt ? `reported ${formatDate(latest.completedAt)}` : '',
              latest.reportCount
                ? `${latest.reportCount} document${latest.reportCount === 1 ? '' : 's'}`
                : 'no report yet',
            ]
              .filter(Boolean)
              .join(' · ')}
          </span>
        </Line>
        {others.length > 0 && (
          <Line>
            <span className="text-gray-400">
              {others.length} earlier inspection{others.length === 1 ? '' : 's'} on this loan
            </span>
          </Line>
        )}
      </div>
    </div>
  )
}

/**
 * The banner every draw surface shows.
 *
 * `forBorrower` drops the flags and the staff links: the borrower sees what was ordered on their
 * draw, not the lender's read of their file. So does anybody else who is not the lender's own
 * staff - an external originator reaches the Draw Request screen and the Construction Reserve tab
 * the same way a draw specialist does, and the backend answers them `staffView: false` with no
 * flags. Both halves are checked here: the flags because they arrive from the server, the links
 * because they lead into Valuations and Servicing.
 */
export const DrawAdvisoryPanel = ({
  advisory,
  loanNumber,
  forBorrower = false,
  className = '',
}: {
  advisory: DrawAdvisoryData | null
  loanNumber: number
  forBorrower?: boolean
  className?: string
}) => {
  if (!advisory) return null

  const staffView = !forBorrower && advisory.staffView === true
  const flags = staffView ? advisory.flags || [] : []
  const stops = flags.filter((f) => f.severity === 'stop')
  const tone = stops.length
    ? 'border-red-200 bg-red-50/60'
    : flags.length
      ? 'border-amber-200 bg-amber-50/60'
      : 'border-gray-200 bg-gray-50'

  return (
    <div className={`rounded border px-4 py-3 ${tone} ${className}`}>
      <InspectionSummary advisory={advisory} loanNumber={loanNumber} staffView={staffView} />

      {forBorrower && advisory.lienWaiverStatus && (
        <div className="mt-2 flex items-start gap-2">
          <InformationCircleIcon className="w-4 h-4 mt-0.5 shrink-0 text-gray-400" />
          <Line>
            <span className="text-gray-500">Lien waiver: </span>
            {advisory.lienWaiverStatus}
          </Line>
        </div>
      )}

      {flags.length > 0 && (
        <>
          <div className="mt-3 border-t border-gray-200/70 pt-2">
            {flags.map((flag) => {
              const link = flagLink(flag, loanNumber)
              const stop = flag.severity === 'stop'
              return (
                <div key={flag.key} className="flex items-start gap-2 py-0.5">
                  {stop ? (
                    <ExclamationTriangleIcon className="w-4 h-4 mt-0.5 shrink-0 text-red-600" />
                  ) : (
                    <InformationCircleIcon className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" />
                  )}
                  <div>
                    <Line>
                      <span className={`font-variation-settings-600 ${stop ? 'text-red-700' : 'text-amber-800'}`}>
                        {flag.label}
                      </span>
                      {link && (
                        <>
                          {' · '}
                          <a href={link} target="_blank" rel="noreferrer" className="text-shade-blue hover:underline">
                            Open
                          </a>
                        </>
                      )}
                    </Line>
                    <Line>
                      <span className="text-gray-600">{flag.detail}</span>
                    </Line>
                  </div>
                </div>
              )
            })}
          </div>
          <div className="mt-2 text-[12px] text-gray-500">
            {stops.length
              ? 'These are reasons a draw is normally refused. None of them stops you recording this one.'
              : 'Worth knowing before you draw. Neither stops you recording this one.'}
          </div>
        </>
      )}
    </div>
  )
}
